import { Box, Chip, Stack, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { Bound } from "lib/@components/binding/Bound"
import { MaintenanceTaskBanner } from "../component-library-slot-definitions"
import { TaskReadOnlyComplianceChip } from "./task-read-only-compliance-chip"
import { TaskReadOnlyHeader } from "./task-read-only-header"
import { TaskReadOnlyHtmlSection } from "./task-read-only-html-section"
import { TaskReadOnlyHyperlinkChip } from "./task-read-only-hyperlink-chip"
import { TaskReadOnlyRelationshipRow } from "./task-read-only-relationship-row"
import { TaskReadOnlyScheduleReferenceChip } from "./task-read-only-schedule-reference-chip"
import { TaskReadOnlySection } from "./task-read-only-section"
import { TaskReadOnlyTagChip } from "./task-read-only-tag-chip"
import { TaskReadOnlyWarning } from "./task-read-only-warning"

export function TaskReadOnlyView({ task, historyItem, historicalMode, canEdit, onEdit }) {
    if (!task) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1">Component not available at this point in time.</Typography>
            </Box>
        )
    }

    return (
        <Stack
            spacing={3}
            sx={{
                p: 3,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: "background.paper",
                minWidth: 0,
                flex: 1,
            }}
        >
            <Bound task={task}>
                <MaintenanceTaskBanner.Slot />
            </Bound>
            <TaskReadOnlyHeader
                task={task}
                historyItem={historyItem}
                historicalMode={historicalMode}
                canEdit={canEdit}
                onEdit={onEdit}
            />

            <TaskReadOnlySection title="Metadata" direction="row">
                {renderMetadataChip("Classification", task.classification)}
                {renderMetadataChip("Category", task.categoryPath)}
                {renderMetadataChip("Timing", formatTimingMinutes(task))}
                {renderMetadataChip("Timing Note", task.timing?.note)}
                {renderMetadataChip("Unit", task.unitOfMeasure)}
            </TaskReadOnlySection>

            <TaskReadOnlyHtmlSection title="Actions" html={task.actionsHtml} />
            <TaskReadOnlyHtmlSection title="Notes" html={task.notesHtml} />

            <If value={task.warnings?.length}>
                <TaskReadOnlySection title="Warnings">
                    <Repeat list={task.warnings} item={<TaskReadOnlyWarning />} />
                </TaskReadOnlySection>
            </If>

            <TaskReadOnlyHtmlSection title="Author's Notes" html={task.authorsNotesHtml} />
            <TaskReadOnlyHtmlSection title="Component Instructions" html={task.componentInstructionsHtml} />

            <If value={task.scheduleReferences?.length}>
                <TaskReadOnlySection title="Schedule References" direction="row">
                    <Repeat list={task.scheduleReferences} item={<TaskReadOnlyScheduleReferenceChip />} />
                </TaskReadOnlySection>
            </If>

            <If value={task.hyperlinksToAdd?.length}>
                <TaskReadOnlySection title="Hyperlinks to Add" direction="row">
                    <Repeat list={task.hyperlinksToAdd} item={<TaskReadOnlyHyperlinkChip />} />
                </TaskReadOnlySection>
            </If>

            <If value={task.relationships?.length}>
                <TaskReadOnlySection title="Relationships">
                    <Repeat list={task.relationships} item={<TaskReadOnlyRelationshipRow />} />
                </TaskReadOnlySection>
            </If>

            <If value={task.compliance?.length}>
                <TaskReadOnlySection title="Compliance" direction="row">
                    <Repeat list={task.compliance} item={<TaskReadOnlyComplianceChip />} />
                </TaskReadOnlySection>
            </If>

            <If value={task.tags?.length}>
                <TaskReadOnlySection title="Tags" direction="row">
                    <Repeat list={task.tags} item={<TaskReadOnlyTagChip />} />
                </TaskReadOnlySection>
            </If>
        </Stack>
    )

    function formatTimingMinutes(currentTask) {
        return currentTask.timing?.minutes ? `${currentTask.timing.minutes} min` : ""
    }

    function renderMetadataChip(label, value) {
        if (!value) return null
        return <Chip size="small" label={`${label}: ${value}`} variant="outlined" />
    }
}
